import adminUsersJson from "@freightsimple/generated-hubble-data/src/Admins.json";
import type { AdminUser } from "@freightsimple/generated-hubble-data/src/Admins";
import { getInitials } from "@freightsimple/ui-core/Helpers/getInitials";

export type Admin = AdminUser;

export const allAdmins: Admin[] = adminUsersJson as Admin[];

export const salesEmailAdmins: Admin[] = allAdmins.filter(
  (a) => a.enableForSalesEmail,
);

export const invoiceAuditAdmins: Admin[] = allAdmins.filter(
  (a) => a.enableForInvoiceAudits,
);

// Eligible to own a company as the Account Exec — drives the "Assigned To" dropdown.
export const accountExecAdmins: Admin[] = allAdmins.filter(
  (a) => a.enableForAccountExec,
);

// Eligible to be the Customer Success contact assisting the AE — drives the
// "Customer Success" dropdown. Mirror of accountExecAdmins.
export const customerSuccessAdmins: Admin[] = allAdmins.filter(
  (a) => a.enableForCustomerSuccess,
);

const adminsByEmail = new Map(allAdmins.map((a) => [a.emailAddress, a]));

/** True when the email belongs to a known FreightSimple admin (so we have a real name for it). */
export function isKnownAdmin(email: string | undefined): boolean {
  return email !== undefined && adminsByEmail.has(email);
}

/** Slack member id (U…) for deep-linking to the admin's DM, if on file. */
export function slackUserIdForAdmin(
  email: string | undefined,
): string | undefined {
  if (!email) return undefined;
  return adminsByEmail.get(email)?.slackUserId;
}

export function isLeadership(email: string | undefined): boolean {
  return (
    email === "chris@freightsimple.com" || email === "natasha@freightsimple.com"
  );
}

export function isSalesTeamMember(email: string | undefined): boolean {
  if (!email) return false;
  return (
    adminsByEmail.get(email)?.enableForSalesEmail === true ||
    isLeadership(email)
  );
}

export function isFinanceTeamMember(email: string | undefined): boolean {
  if (!email) return false;
  return (
    adminsByEmail.get(email)?.enableForInvoiceAudits === true ||
    isLeadership(email)
  );
}

export function isEngineeringTeamMember(email: string | undefined): boolean {
  if (!email) return false;
  return adminsByEmail.get(email)?.department === "Engineering";
}

export function getAdminShortName(email: string): string {
  const { fullName } = getAdmin(email);
  const parts = fullName.trim().split(/\s+/);
  if (parts.length < 2) return parts[0] ?? fullName;
  const first = parts[0];
  const lastInitial = parts[parts.length - 1]!.charAt(0).toUpperCase();
  return `${first} ${lastInitial}.`;
}

export function getAdmin(email: string): Admin {
  const found = adminsByEmail.get(email);
  if (found) return found;

  // Fallback for emails not in the generated admin list: derive a display name from the email
  // prefix (e.g. "jane.doe@…" → "Jane Doe") and blank the rest of the AdminUser fields. Callers
  // today only read fullName, emailAddress, and initials — the other fields are kept present
  // so the return value still satisfies the AdminUser contract.
  const prefix = email.split("@")[0] ?? email;
  const name = prefix
    .split(".")
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(" ");
  return {
    fullName: name,
    emailSignatureName: name,
    emailAddress: email,
    phoneNumber: "",
    mondayUserId: "",
    department: "",
    enableForInvoiceAudits: false,
    enableForSalesEmail: false,
    enableForAccountExec: false,
    enableForCustomerSuccess: false,
    initials: getInitials(name),
  };
}
